import React, { useEffect } from 'react';
import { ShieldCheck, ArrowUpRight, FileCheck2, Landmark } from 'lucide-react';
import SubpageLayout from '../components/FlexiOssCinematic/SubpageLayout';
import ReadAloud from '../components/ReadAloud';
import { HorizontalBorderStack } from '../components/editorial/EditorialLayouts';
import { useTheme } from '../contexts/ThemeContext';
import { trackEvent } from '../lib/analytics';

const EGOV = 'https://e.gov.ph/';

const REGISTRATIONS = [
  { product: 'FlexiOss® Ortho', code: 'CMDR-2025-04943A', scope: 'Orthopedic & trauma bone reconstruction' },
  { product: 'FlexiOss® Dent', code: 'CMDR-2025-04943B', scope: 'Dental, dentoalveolar & maxillofacial grafting' },
];

const VERIFY_POINTS = [
  'The active Certificate of Medical Device Registration (CMDR) number issued for each FlexiOss® product line.',
  'The registered importer and exclusive nationwide distributor of record, Fortschritt Medical Supplies Inc.',
  'The device classification and intended use approved for the Philippine market under prevailing FDA Circulars.',
  'The current registration status and validity window, maintained directly within the national regulatory system.',
];

const FdaVerificationPage = () => {
  const { theme } = useTheme();
  const dark = theme !== 'light';

  useEffect(() => {
    document.title = 'FDA Verification & Institutional Compliance | FlexiOss®';
    window.scrollTo(0, 0);
  }, []);

  const card = dark ? 'border-white/10 bg-slate-900/50' : 'border-slate-200 bg-white';

  return (
    <SubpageLayout productName="FDA Verification">
      {/* Hero */}
      <section data-testid="fda-verification-page" className="relative overflow-hidden bg-slate-950">
        <div className="pointer-events-none absolute -left-20 top-0 h-72 w-72 rounded-full bg-blue-600/20 blur-[120px]" />
        <div className="pointer-events-none absolute right-0 top-10 h-72 w-72 rounded-full bg-[#E87A20]/10 blur-[120px]" />
        <div className="relative mx-auto max-w-5xl px-6 pb-16 pt-16 lg:px-8">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-1.5">
            <ShieldCheck className="h-4 w-4 text-white" strokeWidth={1.8} />
            <span className="font-display text-xs font-bold uppercase tracking-[0.18em] text-white">
              Regulatory Compliance
            </span>
          </div>
          <h1 className="max-w-3xl font-display text-4xl font-black leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
            Verify our credentials on the National eGov Portal.
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300">
            FlexiOss® is a registered medical device with the Philippines Food and Drug Administration. We publish our
            registration numbers openly so any surgeon, hospital procurement officer or patient can confirm our standing
            against the official government record — no intermediaries.
          </p>
          <div className="mt-7"><ReadAloud dark={dark} /></div>
        </div>
      </section>

      {/* Registration numbers */}
      <section className="bg-slate-900 py-14 sm:py-16">
        <div className="mx-auto max-w-5xl px-6 lg:px-8">
          <p className="font-display text-[11px] font-bold uppercase tracking-[0.24em] text-blue-400">
            Certificates of Medical Device Registration
          </p>
          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            {REGISTRATIONS.map((r) => (
              <div key={r.code} data-testid={`fda-reg-${r.code}`} className={`rounded-2xl border p-7 ${card}`}>
                <div className="mb-4 flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-600 text-white">
                    <FileCheck2 className="h-5 w-5" strokeWidth={1.7} />
                  </div>
                  <h3 className="text-lg font-black tracking-tight text-white">{r.product}</h3>
                </div>
                <p className="font-mono text-xl font-bold tracking-tight text-blue-400">{r.code}</p>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{r.scope}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What you can verify */}
      <section className="bg-slate-900 pb-16">
        <div className="mx-auto max-w-5xl px-6 lg:px-8">
          <div className={`rounded-2xl border p-7 sm:p-9 ${card}`}>
            <h2 className="mb-6 font-display text-2xl font-black tracking-tight text-white sm:text-3xl">
              What the government record confirms
            </h2>
            <HorizontalBorderStack items={VERIFY_POINTS} accent="#3b82f6" testid="fda-verify-points" />
          </div>
        </div>
      </section>

      {/* eGov CTA + memorandum context */}
      <section className="bg-slate-950 py-16">
        <div className="mx-auto max-w-5xl px-6 lg:px-8">
          <div className={`relative overflow-hidden rounded-3xl border p-8 sm:p-12 ${dark ? 'border-blue-500/25 bg-gradient-to-br from-[#04101f] to-slate-950' : 'border-blue-300/50 bg-gradient-to-br from-blue-50 to-white'}`}>
            <div className="pointer-events-none absolute left-0 top-0 h-px w-full bg-gradient-to-r from-blue-500/60 to-transparent" />
            <div className="flex flex-col gap-7 lg:flex-row lg:items-center lg:justify-between">
              <div className="max-w-xl">
                <div className="mb-4 flex items-center gap-3">
                  <Landmark className="h-6 w-6 text-blue-400" strokeWidth={1.7} />
                  <span className="font-display text-xs font-bold uppercase tracking-[0.2em] text-blue-400">
                    National eGov Portal System
                  </span>
                </div>
                <h3 className="font-display text-2xl font-black leading-tight tracking-tight text-white sm:text-3xl">
                  Check our compliance at the source.
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-slate-300">
                  The eGov PH platform consolidates Philippine government services, including regulatory and FDA-related
                  verifications, under one official portal. Registration of imported medical devices and the conduct of
                  their distributors are governed by prevailing FDA Circulars; the eGov system is the public entry point
                  for confirming that standing in real time.
                </p>
              </div>
              <a
                href={EGOV}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="egov-portal-link"
                onClick={() => trackEvent('verify_egov_portal', { source: 'fda_verification' })}
                className="group inline-flex flex-shrink-0 items-center gap-2.5 rounded-full bg-blue-600 px-7 py-4 text-base font-bold text-white transition-all duration-300 hover:bg-blue-500 hover:shadow-[0_0_40px_rgba(37,99,235,0.45)]"
              >
                Open eGov Portal
                <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.2} />
              </a>
            </div>
          </div>
          <p className="mx-auto mt-8 max-w-3xl text-center text-xs leading-relaxed text-slate-500">
            Registration numbers and statuses are maintained by the Philippines Food and Drug Administration and may be
            updated through new Circulars or directives. Always confirm the current record directly on the official
            government portal.
          </p>
        </div>
      </section>
    </SubpageLayout>
  );
};

export default FdaVerificationPage;
